import { cn } from '@/lib/ui/cn';
import { cva } from 'class-variance-authority';
import { ReactNode } from 'react';

type Variant =
  | 'h1'
  | 'h1-serif'
  | 'h2'
  | 'h2-serif'
  | 'h3'
  | 'h3-serif'
  | 'body'
  | 'body-small'
  | 'quote';
type As = 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'div';

type FontColors = 'black' | 'dark' | 'semidark' | 'muted';

function getDefaultAs(variant: Variant): As {
  switch (variant) {
    case 'h1':
    case 'h1-serif':
      return 'h1';
    case 'h2':
    case 'h2-serif':
      return 'h2';
    case 'h3':
    case 'h3-serif':
      return 'h3';
    default:
      return 'p';
  }
}

interface UIBaseProps {
  className?: string;
  children?: string | ReactNode | ReactNode[];
}

const variants = cva('', {
  variants: {
    variant: {
      h1: [
        'font-sans',
        'text-[32px]',
        'lg:text-[68px]',
        'leading-[100%]',
        'tracking-[-2%]',
        'lg:tacking-[-4%]',
      ],
      'h1-serif': [
        'font-serif',
        'text-[36px]',
        'lg:text-[72px]',
        'leading-[100%]',
      ],
      h2: [
        'font-sans',
        'text-[27px]',
        'lg:text-[60px]',
        'leading-[100%]',
        'lg:leading-[72px]',
        'tracking-[-2%]',
        'lg:tacking-[-4%]',
      ],
      'h2-serif': [
        'font-serif',
        'text-[27px]',
        'lg:text-[60px]',
        'leading-[31px]',
        'lg:leading-[110%]',
      ],
      h3: [
        'font-sans',
        'text-[24px]',
        'lg:text-[32px]',
        'leading-[100%]',
        'lg:leading-[130%]',
        'tracking-[-2%]',
        'lg:tacking-[-4%]',
      ],
      'h3-serif': [
        'font-serif',
        'text-[24px]',
        'lg:text-[36px]',
        'leading-[100%]',
        'lg:leading-[130%]',
      ],
      body: ['font-sans', 'text-[18px]', 'lg:text-[21px]', 'leading-[130%]'],
      'body-small': [
        'font-sans',
        'text-[12px]',
        'lg:text-[16px]',
        'leading-[130%]',
      ],
      quote: [
        'font-serif',
        'text-[21px]',
        'lg:text-[36]',
        'leading-[31px]',
        'lg:leading-[130%]',
      ],
    },
    color: {
      black: '',
      dark: 'text-[#292722]',
      semidark: 'text-[#231F20]',
      muted: 'text-[#BBBBBB]',
    },
    bold: {
      false: '',
      true: 'font-bold',
    },
    strike: {
      false: '',
      true: 'line-through',
    },
  },
  compoundVariants: [
    { variant: 'h1', bold: false, class: 'font-medium' },
    { variant: 'h1-serif', bold: false, class: 'font-light' },
    { variant: 'h2', bold: false, class: 'font-medium' },
    { variant: 'h2-serif', bold: false, class: 'font-light' },
    { variant: 'h3', bold: false, class: 'font-medium' },
    { variant: 'h3-serif', bold: false, class: 'font-light' },
    { variant: 'body', bold: false, class: 'font-normal' },
    { variant: 'body-small', bold: false, class: 'font-normal' },
    { variant: 'quote', bold: false, class: 'font-light' },
  ],
});

export function Text({
  variant,
  color = 'black',
  bold = false,
  strike = false,
  as,
  className,
  children,
}: {
  variant: Variant;
  color?: FontColors;
  as?: As;
  bold?: boolean;
  strike?: boolean;
} & UIBaseProps) {
  const classes = cn(
    variants({
      variant,
      color,
      bold,
      strike,
    }),
    className
  );

  switch (as ?? getDefaultAs(variant)) {
    case 'h1':
      return <h1 className={classes}>{children}</h1>;
    case 'h2':
      return <h2 className={classes}>{children}</h2>;
    case 'h3':
      return <h3 className={classes}>{children}</h3>;
    case 'p':
      return <p className={classes}>{children}</p>;
    case 'span':
      return <span className={classes}>{children}</span>;
    case 'div':
      return <div className={classes}>{children}</div>;
  }
}

export const Typography = Text;
